import { get } from 'lodash';
import moment from 'moment';
import format from 'date-fns/format';
import { daysAdded } from '../../util/dates';
import { getListingImages, getPackageImages } from '../../util/listing';
import { formatItineraryGuestSize, getPackageItineraryData } from '../../util/transaction';
import { getInquiryGuestCount } from './inquiryPricingModel';

export const INQUIRY_MESSAGE_MAX_LENGTH = 1000;

const imageUrl = image => {
  const variants = get(image, 'attributes.variants', {});
  return (
    variants['scaled-small']?.url ||
    variants['landscape-crop']?.url ||
    variants.facebook?.url ||
    variants.default?.url ||
    ''
  );
};

export const hostLabel = displayName => {
  if (!displayName) {
    return '';
  }

  const [firstName, lastName] = displayName.split(' ');
  if (!lastName) {
    return firstName;
  }

  return `${firstName} ${lastName.charAt(0)}.`;
};

export const formatCalendarInfoDays = days => {
  if (!days) {
    return null;
  }

  if (days.min === days.max) {
    return days.min;
  }

  if (days.max === 365) {
    return `${days.min}+`;
  }

  return `${days.min}-${days.max}`;
};

export const isInquirySubmitDisabled = ({ invalid, disabled, startDate, endDate, message }) => {
  const trimmed = typeof message === 'string' ? message.trim() : '';
  return Boolean(invalid || disabled || !startDate || !endDate || !trimmed);
};

/**
 * Trip dates for the summary card. Uses the same inputs and helper as prod's inquiry
 * itinerary (InquiryItinerary -> PackageItinerary -> getPackageItineraryData), so the
 * check-in / hunting days / check-out display and day counts match prod exactly.
 * `hasLodging` must be the dynamic value from useInquiryLodging (package lodging or
 * lodging add-ons, and a trip longer than one day), as in InquiryItinerary.
 */
export const describeInquiryItinerary = ({
  listing,
  activity,
  startDate,
  endDate,
  startLodgingDate,
  hasLodging,
} = {}) => {
  if (!startDate || !endDate) {
    return null;
  }

  const itinerary = getPackageItineraryData({
    activity,
    listing,
    startDate: moment(startDate).format('YYYY-MM-DD'),
    endDate: moment(endDate).format('YYYY-MM-DD'),
    hasLodging: Boolean(hasLodging),
    isProvider: false,
    arrivalInstructions: get(listing, 'attributes.publicData.arrivalInstructions'),
    startLodgingDate: startLodgingDate ? moment(startLodgingDate).format('YYYY-MM-DD') : null,
  });
  return { ...itinerary, activity };
};

/**
 * Required trip details fields that cannot rely on final-form field errors alone
 * (the date picker has no error state of its own).
 */
export const getInquiryTripDetailsErrors = ({ startDate, endDate, message, partySize }) => {
  const errors = {};
  if (!startDate || !endDate) {
    errors.dates = 'InquiryWizard.datesRequired';
  }
  if (typeof message !== 'string' || !message.trim()) {
    errors.message = 'InquiryWizard.messageRequired';
  }
  if (partySize === null || partySize === undefined || partySize === '') {
    errors.partySize = 'InquiryWizard.partySizeRequired';
  }
  return errors;
};

export const describeInquiryDates = (intl, startDate, endDate, dayCount) => {
  const hasRange = Boolean(startDate && endDate);
  const isSingleDay = hasRange ? daysAdded(startDate, endDate) === 1 : false;
  const formattedStartDate = startDate ? moment(startDate).format('YYYY-MM-DD') : null;
  const formattedEndDate = endDate ? moment(endDate).format('YYYY-MM-DD') : null;
  let dateSummary = '';
  let bookingDateText = null;

  if (hasRange) {
    const countedDays = dayCount == null ? daysAdded(startDate, endDate) : dayCount;
    const range = isSingleDay
      ? format(startDate, 'MMM DD, YYYY')
      : `${format(startDate, 'MMM DD')} – ${format(endDate, 'MMM DD, YYYY')}`;
    dateSummary = intl.formatMessage(
      { id: 'InquiryWizard.datesSummary' },
      { dates: range, dayCount: countedDays }
    );
    bookingDateText = isSingleDay
      ? format(startDate, 'MMM DD')
      : `${format(startDate, 'MMM DD')} - ${format(endDate, 'MMM DD, YYYY')}`;
  }

  return {
    isSingleDay,
    formattedStartDate,
    formattedEndDate,
    dateSummary,
    bookingDateText,
  };
};

export const buildInquirySummary = ({ listing, formValues }) => {
  const selectedPackage = formValues?.selectedPackage;
  const displayName = get(listing, 'author.attributes.profile.displayName', '');
  const firstName = displayName ? displayName.split(' ')[0] : '';
  const landownerName = firstName || 'the landowner';
  const listingTitle = get(listing, 'attributes.title', '');
  const packageTitle = selectedPackage?.title || listingTitle;
  const hostedBy = hostLabel(displayName);
  const subtitle = hostedBy ? `${packageTitle} · Hosted by ${hostedBy}` : packageTitle;
  const packagesImages = selectedPackage?.id ? getPackageImages(listing, selectedPackage.id) : [];
  const listingImages = getListingImages(listing);
  const firstImage =
    (packagesImages && packagesImages[0]) || listingImages[0] || get(listing, 'images[0]', null);
  const guestCount = getInquiryGuestCount(
    formValues?.partySize,
    get(selectedPackage, 'guests.min', 1)
  );
  const details = [
    {
      // Same icon and text as the inbox Trip details guests row (PackageItinerary).
      icon: 'User',
      label: formatItineraryGuestSize(guestCount),
    },
  ];

  return {
    listingTitle,
    packageTitle,
    subtitle,
    image: {
      src: imageUrl(firstImage),
      alt: packageTitle,
    },
    details,
    landownerName,
    firstName,
    guestCount,
  };
};
